import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, IconButton, Paper, Stack, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { X } from 'lucide-react';
import type { DateRange } from 'react-day-picker';
import { differenceInCalendarDays, format } from 'date-fns';
import appointmentCalendarFilterIcon from '@/assets/images/appointments/appointment-calendar-filter.svg';
import NotificationCenter from '@/components/notifications/NotificationCenter';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DASHBOARD_ANALYTICS_DATE_RANGE_MAX_DAYS } from './dashboardHomeTokens';

export interface DashboardHomeHeaderProps {
  authLoading: boolean;
  greetingLine: string;
  dateLine: string;
  analyticsDateRange?: DateRange;
  analyticsDateRangeLabel: string;
  /** Analytics date-range picker — only on Practice Insights tab. */
  showAnalyticsDateRangeControl?: boolean;
  onAnalyticsDateRangeChange: (range: DateRange | undefined) => void;
  onAnalyticsPresetSelect: (preset: 'lastWeek' | 'lastMonth') => void;
  onClearAnalyticsDateRange: () => void;
}

export const DashboardHomeHeader: React.FC<DashboardHomeHeaderProps> = ({
  authLoading,
  greetingLine,
  dateLine,
  analyticsDateRange,
  analyticsDateRangeLabel,
  showAnalyticsDateRangeControl = false,
  onAnalyticsDateRangeChange,
  onAnalyticsPresetSelect,
  onClearAnalyticsDateRange,
}) => {
  const [rangePickerOpen, setRangePickerOpen] = useState(false);
  const [draftDateRange, setDraftDateRange] = useState<DateRange | undefined>(analyticsDateRange);
  const isCompactRangePicker = useMediaQuery('(max-width:700px)');
  const maxSelectableDate = useMemo(() => new Date(), [rangePickerOpen]);

  const handleRangeSelect = (range: DateRange | undefined) => {
    setDraftDateRange(range);
  };

  useEffect(() => {
    if (rangePickerOpen) {
      setDraftDateRange(analyticsDateRange);
    }
  }, [analyticsDateRange, rangePickerOpen]);

  useEffect(() => {
    if (!showAnalyticsDateRangeControl) {
      setRangePickerOpen(false);
    }
  }, [showAnalyticsDateRangeControl]);

  const draftStartLabel = draftDateRange?.from ? format(draftDateRange.from, 'MMM d, yyyy') : 'Start date';
  const draftEndLabel = draftDateRange?.to ? format(draftDateRange.to, 'MMM d, yyyy') : 'End date';
  const draftDayCount = useMemo(() => {
    if (!draftDateRange?.from || !draftDateRange.to) return undefined;
    return Math.abs(differenceInCalendarDays(draftDateRange.to, draftDateRange.from)) + 1;
  }, [draftDateRange]);
  const draftRangeExceedsMax =
    typeof draftDayCount === 'number' && draftDayCount > DASHBOARD_ANALYTICS_DATE_RANGE_MAX_DAYS;
  const canApplyDraftRange = Boolean(draftDateRange?.from && draftDateRange.to && !draftRangeExceedsMax);

  const handleClearAnalyticsDateRange = (event?: React.MouseEvent) => {
    event?.stopPropagation();
    event?.preventDefault();
    onClearAnalyticsDateRange();
    setDraftDateRange(undefined);
    setRangePickerOpen(false);
  };

  const analyticsDateRangeShortLabel = useMemo(() => {
    if (!analyticsDateRange?.from) return '';
    const from = analyticsDateRange.from;
    const to = analyticsDateRange.to ?? analyticsDateRange.from;
    if (from.getTime() === to.getTime()) {
      return format(from, 'MMM d, yyyy');
    }
    return `${format(from, 'MMM d')} - ${format(to, 'MMM d')}`;
  }, [analyticsDateRange]);
  const analyticsDateRangeTooltip = analyticsDateRangeShortLabel
    ? `Analytics date range: ${analyticsDateRangeLabel}. Maximum ${DASHBOARD_ANALYTICS_DATE_RANGE_MAX_DAYS} days, up to today.`
    : `Choose analytics date range. Maximum ${DASHBOARD_ANALYTICS_DATE_RANGE_MAX_DAYS} days, up to today.`;

  return (
    <Paper
      elevation={0}
      sx={{
        borderBottom: '1px solid rgba(0,0,0,0.08)',
        borderRadius: 0,
        bgcolor: '#fff',
        px: { xs: 2, sm: 3 },
        py: 1.5,
      }}
    >
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ lg: 'center' }} justifyContent="space-between">
        <Stack spacing={0.75}>
          <Typography variant="h5" sx={{ fontWeight: 500, color: '#000', textTransform: 'capitalize' }}>
            {authLoading ? <Box component="span" sx={{ opacity: 0.5 }}>Loading...</Box> : greetingLine}
          </Typography>
          <Typography sx={{ color: '#000', fontSize: '1rem' }}>{dateLine}</Typography>
        </Stack>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="nowrap" sx={{ minWidth: 0 }}>
          {showAnalyticsDateRangeControl ? (
          <Popover open={rangePickerOpen} onOpenChange={setRangePickerOpen}>
            <Tooltip title={analyticsDateRangeTooltip}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  title={analyticsDateRangeTooltip}
                  aria-label={
                    analyticsDateRangeShortLabel
                      ? `Analytics date range: ${analyticsDateRangeShortLabel}. Maximum ${DASHBOARD_ANALYTICS_DATE_RANGE_MAX_DAYS} days.`
                      : `Choose analytics date range. Maximum ${DASHBOARD_ANALYTICS_DATE_RANGE_MAX_DAYS} days.`
                  }
                  sx={{
                    flexShrink: 0,
                    minWidth: 36,
                    width: analyticsDateRangeShortLabel ? 'auto' : 36,
                    height: 36,
                    px: analyticsDateRangeShortLabel ? 1.25 : 0,
                    pr: analyticsDateRangeShortLabel ? 0.75 : 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    borderRadius: '6px',
                    bgcolor: analyticsDateRangeShortLabel ? '#F0F9FF' : '#fff',
                    border: analyticsDateRangeShortLabel ? '1px solid #2FA8D5' : '1px solid #e8e8e8',
                    color: analyticsDateRangeShortLabel ? '#0284C7' : '#475467',
                    textTransform: 'none',
                    boxShadow: '0px 2px 4px rgba(0,0,0,0.08)',
                    '&:hover': {
                      bgcolor: analyticsDateRangeShortLabel ? '#E0F2FE' : '#F8FAFC',
                      borderColor: analyticsDateRangeShortLabel ? '#0284C7' : '#BFC7D2',
                      boxShadow: '0px 2px 4px rgba(0,0,0,0.08)',
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={appointmentCalendarFilterIcon}
                    alt=""
                    aria-hidden
                    sx={{ width: 20, height: 20, objectFit: 'contain', display: 'block', flexShrink: 0 }}
                  />
                  {analyticsDateRangeShortLabel ? (
                    <>
                      <Typography
                        component="span"
                        sx={{
                          fontSize: 13,
                          fontWeight: 500,
                          fontFamily: 'Inter, sans-serif',
                          whiteSpace: 'nowrap',
                          lineHeight: 1.2,
                        }}
                      >
                        {analyticsDateRangeShortLabel}
                      </Typography>
                      <IconButton
                        size="small"
                        aria-label="Clear analytics date range"
                        onClick={handleClearAnalyticsDateRange}
                        onPointerDown={(event) => event.stopPropagation()}
                        sx={{
                          width: 22,
                          height: 22,
                          p: 0,
                          color: '#667085',
                          '&:hover': { bgcolor: 'rgba(2, 132, 199, 0.08)', color: '#0284C7' },
                        }}
                      >
                        <X size={14} strokeWidth={2.25} />
                      </IconButton>
                    </>
                  ) : null}
                </Button>
              </PopoverTrigger>
            </Tooltip>
            <PopoverContent className="w-auto p-0" align="end" sideOffset={8}>
              <Box sx={{ width: isCompactRangePicker ? 'min(360px, calc(100vw - 24px))' : 652, maxWidth: 'calc(100vw - 24px)', overflow: 'hidden' }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 1,
                    p: 1.5,
                    borderBottom: '1px solid #eef2f7',
                    bgcolor: '#fbfdff',
                  }}
                >
                  {[
                    { label: 'Start', value: draftStartLabel, active: Boolean(draftDateRange?.from && !draftDateRange.to) },
                    { label: 'End', value: draftEndLabel, active: Boolean(draftDateRange?.from && draftDateRange.to) },
                  ].map((item) => (
                    <Box
                      key={item.label}
                      sx={{
                        minHeight: 58,
                        border: `1px solid ${item.active ? '#20aedc' : '#dce7ef'}`,
                        borderRadius: '8px',
                        px: 1.25,
                        py: 0.9,
                        bgcolor: item.active ? '#f1fbff' : '#fff',
                      }}
                    >
                      <Typography sx={{ fontSize: 12, lineHeight: '16px', fontWeight: 600, color: '#667085' }}>{item.label}</Typography>
                      <Typography sx={{ mt: 0.25, fontSize: 13, lineHeight: '18px', fontWeight: 600, color: '#1f2937' }}>{item.value}</Typography>
                    </Box>
                  ))}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, p: 1.5, pb: 0.5 }}>
                  <Button
                    size="small"
                    variant={!analyticsDateRange?.from ? 'contained' : 'outlined'}
                    onClick={() => {
                      onClearAnalyticsDateRange();
                      setDraftDateRange(undefined);
                      setRangePickerOpen(false);
                    }}
                    sx={{
                      minWidth: 0,
                      flex: 1,
                      borderRadius: '8px',
                      borderColor: '#d9edf7',
                      color: !analyticsDateRange?.from ? '#fff' : '#175b73',
                      textTransform: 'none',
                      fontSize: 12,
                      fontWeight: 600,
                      bgcolor: !analyticsDateRange?.from ? '#20aedc' : '#f7fcff',
                      boxShadow: 'none',
                      '&:hover': {
                        borderColor: '#20aedc',
                        bgcolor: !analyticsDateRange?.from ? '#1a9bc6' : '#eef9fd',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    Last 30 days
                  </Button>
                {[
                  { label: 'Last 7 days', value: 'lastWeek' as const },
                ].map((preset) => (
                  <Button
                    key={preset.value}
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      onAnalyticsPresetSelect(preset.value);
                      setRangePickerOpen(false);
                    }}
                    sx={{
                      minWidth: 0,
                      flex: 1,
                      borderRadius: '8px',
                      borderColor: '#d9edf7',
                      color: '#175b73',
                      textTransform: 'none',
                      fontSize: 12,
                      fontWeight: 600,
                      bgcolor: '#f7fcff',
                      '&:hover': { borderColor: '#20aedc', bgcolor: '#eef9fd' },
                    }}
                    >
                      {preset.label}
                    </Button>
                  ))}
                </Box>
                <Box sx={{ overflowX: 'auto' }}>
                  <Calendar
                    initialFocus
                    mode="range"
                    defaultMonth={draftDateRange?.from ?? analyticsDateRange?.from}
                    selected={draftDateRange}
                    onSelect={handleRangeSelect}
                    max={DASHBOARD_ANALYTICS_DATE_RANGE_MAX_DAYS}
                    disabled={{ after: maxSelectableDate }}
                    toDate={maxSelectableDate}
                    numberOfMonths={isCompactRangePicker ? 1 : 2}
                    className="p-3"
                    classNames={{
                      months: 'flex flex-col sm:flex-row gap-4 sm:gap-4 sm:space-x-0 sm:space-y-0',
                      month: 'space-y-3',
                      caption: 'flex justify-center pt-1 relative items-center',
                      caption_label: 'text-sm font-semibold',
                      nav_button: 'h-8 w-8 bg-transparent p-0 opacity-60 hover:opacity-100',
                      table: 'w-full border-collapse',
                      head_row: 'flex',
                      head_cell: 'text-muted-foreground rounded-md w-10 font-medium text-[0.75rem]',
                      row: 'flex w-full mt-1',
                      cell: 'h-10 w-10 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20',
                      day: 'h-10 w-10 p-0 font-normal aria-selected:opacity-100 transition-all',
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    borderTop: '1px solid #eef2f7',
                    px: 1.5,
                    py: 1,
                  }}
                >
                  <Typography sx={{ fontSize: 12, color: draftRangeExceedsMax ? '#dc2626' : '#475467', minWidth: 0 }}>
                    {draftRangeExceedsMax
                      ? `Select ${DASHBOARD_ANALYTICS_DATE_RANGE_MAX_DAYS} days or less`
                      : draftDayCount
                        ? `${draftDayCount} day${draftDayCount === 1 ? '' : 's'} selected`
                        : `Maximum ${DASHBOARD_ANALYTICS_DATE_RANGE_MAX_DAYS} days, up to today`}
                  </Typography>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setRangePickerOpen(false)}
                      sx={{ minWidth: 0, textTransform: 'none', fontSize: 12, color: '#667085' }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={!canApplyDraftRange}
                      onClick={() => {
                        if (!draftDateRange?.from || !draftDateRange.to) return;
                        onAnalyticsDateRangeChange(draftDateRange);
                        setRangePickerOpen(false);
                      }}
                      sx={{
                        minWidth: 0,
                        textTransform: 'none',
                        fontSize: 12,
                        fontWeight: 600,
                        borderRadius: '8px',
                        bgcolor: '#20aedc',
                        boxShadow: 'none',
                        '&:hover': { bgcolor: '#1a9bc6', boxShadow: 'none' },
                      }}
                    >
                      Apply
                    </Button>
                  </Stack>
                </Box>
              </Box>
            </PopoverContent>
          </Popover>
          ) : null}
          <NotificationCenter
            buttonTestId="dashboard-header-notifications"
            buttonVariant="feature"
          />
        </Stack>
      </Stack>
    </Paper>
  );
};
